import { useCallback, useEffect, useState } from "react";
import {
    Autocomplete,
    Box,
    Button,
    Card,
    MenuItem,
    TextField,
    Typography
} from "@mui/material";
import RestAPIService from "../../../../Utility/Services/RestAPIService";
import { Serviceurls } from "../../../../Utility/API/Serviceurls";
import ShowComponents from "../Common/ShowComponent";
import { Colors } from "../../../../Styles/Colors";
import { renderTitle } from "../Common/DocCommonComponent";

const DocTentativePlan = ({
    Title,
    appointmentId,
    ipAdmissionId,
    clinicId,
    doctorId,
    isFromIp,
    isSelected,
    successMessage,
    errorMessage,
    patientId
}) => {
    const [state, setState] = useState({
        id: null,
        recommendationType: "",
        ipDays: "",
        opDays: "",
        instructions: "",
        status: "Pending",
        isLoading: false,
        isTransmitted: false,
        patientType: "Regular",
    });
    const patientTypes = ["Regular", "Vulnerabilities"];
    const updateState = (obj) =>
        setState((prev) => ({ ...prev, ...obj }));

    /* ================= FETCH ================= */
    const getPlan = useCallback(() => {
        const params = isFromIp ? `?ip_admission_id=${ipAdmissionId}&clinic_id=${clinicId}&doctor_id=${doctorId}` : `?appointment_id=${appointmentId}`;
        RestAPIService.getAll(
            Serviceurls.AYURVEDA_TENTATIVE_PLAN + params
        )
            .then((res) => {
                if (res?.data?.status === "success" && res.data.data) {
                    const d = res.data.data;
                    updateState({
                        treatmentDays: d.treatment_days,
                        recommendationType: d.recommendation_type,
                        ipDays: d.ip_days,
                        opDays: d.op_days,
                        status: d.status,
                        patientType: d.patient_type,
                        instructions: d.instructions || "",
                    });
                }
            })
            .catch((err) => errorMessage(err?.message));
    }, [appointmentId, isFromIp, ipAdmissionId]);

    /* ================= SAVE ================= */
    const savePlan = () => {
        if (!state.recommendationType && (!state.ipDays || !state.opDays ) && !state.treatmentDays)
            return errorMessage("Please fill mandatory fields");

        const payload = {
            patient_id: patientId,
            treatment_days: Number(state.treatmentDays),
            recommendation_type: state.recommendationType,
            ip_days: state.ipDays ? Number(state.ipDays) : 0,
            op_days: state.opDays ? Number(state.opDays) : 0,
            instructions: state.instructions,
            patient_type: state.patientType
        };
        if (isFromIp) {
            payload.ip_admission_id = ipAdmissionId;
            payload.clinic_id = clinicId;
            payload.doctor_id = doctorId;
        } else {
            payload.appointment_id = appointmentId;
        }

        updateState({ isLoading: true });

        const method = state.id ? "NewupdateWithOutId" : "create";

        RestAPIService[method](payload, Serviceurls.AYURVEDA_TENTATIVE_PLAN)
            .then((res) => {
                if (res.data.status === "success") {
                    successMessage(res.data.message);
                    getPlan();
                } else {
                    errorMessage(res.data.message);
                }
            })
            .catch((err) =>
                errorMessage(err?.response?.data?.message || err.message)
            )
            .finally(() => updateState({ isLoading: false }));
    };

    /* ================= CLEAR ================= */
    const clearForm = () => {
        updateState({
            id: null,
            treatmentDays: "",
            recommendationType: "",
            ipDays: "",
            opDays: "",
            instructions: "",
            patientType: "Regular",
        });
    };

    useEffect(() => {
        getPlan();
    }, [getPlan]);

    return (
        <ShowComponents onIntersection={getPlan}>
            <Card
                sx={{
                    width: "100%",
                    border: `2px solid ${isSelected ? Colors.themeDark : Colors.white
                        }`,
                    padding: "0.5vw"
                }}
            >
                {/* ===== HEADER ===== */}
                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    borderBottom="1px solid #e0e0e0"
                    padding="0.5vw"
                >
                    {renderTitle(Title || "Tentative Treatment Plan", isSelected)}

                    <Box display="flex" gap="0.8vw">
                        <Button
                            variant="outlined"
                            onClick={clearForm}
                            disabled={state.status !== "Pending"}
                        >
                            CLEAR
                        </Button>
                        <Button
                            variant="contained"
                            onClick={savePlan}
                            disabled={state.isLoading || state.status !== "Pending"}
                        >
                            SAVE
                        </Button>
                    </Box>
                </Box>

                {/* ===== FORM ROW ===== */}
                <Box
                    display="grid"
                    gridTemplateColumns="repeat(4, 1fr)"
                    gap="1vw"
                    mt={1}
                >
                    <TextField
                        select
                        label="Recommendation Type *"
                        size='small'
                        value={state.recommendationType}
                        onChange={(e) =>
                            updateState({ recommendationType: e.target.value })
                        }
                        disabled={state.status !== "Pending"}
                    >   
                        <MenuItem value="IP">IP</MenuItem>
                        <MenuItem value="WALKIN">Walk-In</MenuItem>
                    </TextField>

                    <TextField
                        label="IP Days"
                        type="number"
                        size='small'
                        value={state.ipDays}
                        onChange={(e) =>
                            updateState({ ipDays: e.target.value })
                        }
                        disabled={state.status !== "Pending"}
                    />

                    <TextField
                        label="Walkin Days"
                        type="number"
                        size='small'
                        value={state.opDays}
                        onChange={(e) =>
                            updateState({ opDays: e.target.value })
                        }
                        disabled={state.status !== "Pending"}
                    />
                    <Autocomplete
                        options={patientTypes}
                        value={state.patientType}
                        onChange={(e, val) => updateState({patientType: val })}
                        renderInput={(params) => <TextField {...params} label="Patient Type *" size="small" />}
                        sx={{ width: "13vw" }}
                    />
                </Box>

                {/* ===== INSTRUCTIONS ===== */}
                <Box mt={1.2}>
                    <Typography fontWeight={600} mb={0.5}>
                        Description
                    </Typography>

                    <TextField
                        multiline
                        rows={6}
                        fullWidth
                        value={state.instructions}
                        disabled={state.status !== "Pending"}
                        onChange={(e) =>
                            updateState({ instructions: e.target.value })
                        }
                        placeholder="Enter diet instructions here..."
                    />
                </Box>
            </Card>
        </ShowComponents>
    );
};

export default DocTentativePlan;
